import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Avatar,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PhotoCamera, Timeline as TimelineIcon, Settings as SettingsIcon, Link as LinkIcon, ContentCopy as ContentCopyIcon, Email as EmailIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import AuthContext from '../context/AuthContext';
import RelationshipTimeline from '../components/RelationshipTimeline';

const RelationshipPage = () => {
  const { backendUrl, authTokens, profile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const relationshipIdParam = searchParams.get('id');
  const [relationship, setRelationship] = useState({
    relationship_id: '',
    startDate: '',
    endDate: '',
    partnerName: '',
    partnerEmail: '',
    partnerSex: '',
    partnerDateOfBirth: null,
    comments: '',
    relationship_picture: null,
    invitation_sent: null,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [errors, setErrors] = useState({});
  const [relationshipPictureFile, setRelationshipPictureFile] = useState(null);
  const [relationshipPicturePreview, setRelationshipPicturePreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [lastEndedRelationshipDate, setLastEndedRelationshipDate] = useState(null);
  const [minAllowedDate, setMinAllowedDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [linkCode, setLinkCode] = useState('');
  const [isLinked, setIsLinked] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [partnerLinkCode, setPartnerLinkCode] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [savedPartnerEmail, setSavedPartnerEmail] = useState('');
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [linkedToName, setLinkedToName] = useState('');

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidEmailForInvite = () => {
    // Only show invite button if email is valid format, no errors, and matches saved email
    return relationship.partnerEmail &&
           relationship.partnerEmail.trim() &&
           validateEmail(relationship.partnerEmail) &&
           !errors.partnerEmail &&
           relationship.partnerEmail === savedPartnerEmail;
  };

  const handleRelationshipPictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRelationshipPictureFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setRelationshipPicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadRelationshipPicture = async () => {
    if (!relationshipPictureFile) return;

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('relationship_picture', relationshipPictureFile);

      const response = await fetch(`${backendUrl}api/upload-relationship-picture/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authTokens?.access}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload relationship picture');
      }

      const result = await response.json();

      // Update the relationship state with the new picture
      setRelationship(prev => ({
        ...prev,
        relationship_picture: result.relationship_picture
      }));

      // Clear the preview and file
      setRelationshipPictureFile(null);
      setRelationshipPicturePreview(null);

      showSnackbar('Relationship picture updated successfully!', 'success');
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  const fetchLinkStatus = async (relationshipId) => {
    try {
      const response = await fetch(`${backendUrl}api/relationship-link-status/${relationshipId}/`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLinked(data.is_linked);
        setLinkCode(data.link_code || '');
        setLinkedToName(data.linked_to?.partner_name || '');
      }
    } catch (error) {
      console.error('Error fetching link status', error);
    }
  };

  const handleGenerateLinkCode = async () => {
    try {
      const response = await fetch(`${backendUrl}api/generate-relationship-link-code/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ relationship_id: relationship.relationship_id }),
      });

      if (response.ok) {
        const data = await response.json();
        setLinkCode(data.link_code);
        showSnackbar('Link code generated successfully!', 'success');
      } else {
        throw new Error('Failed to generate link code');
      }
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleLinkRelationships = async () => {
    if (!partnerLinkCode.trim()) {
      showSnackbar('Please enter a link code', 'error');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}api/link-relationships/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({
          relationship_id: relationship.relationship_id,
          link_code: partnerLinkCode.toUpperCase(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsLinked(true);
        setLinkDialogOpen(false);
        setPartnerLinkCode('');
        showSnackbar(data.message, 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to link relationships');
      }
    } catch (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const handleCopyLinkCode = () => {
    navigator.clipboard.writeText(linkCode);
    showSnackbar('Link code copied to clipboard!', 'success');
  };

  const handleSendInvitation = async () => {
    setSendingInvitation(true);
    try {
      const response = await fetch(`${backendUrl}api/send-partner-invitation/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ relationship_id: relationship.relationship_id }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update the relationship state to mark invitation as sent
        setRelationship(prev => ({
          ...prev,
          invitation_sent: new Date().toISOString(),
        }));
        showSnackbar(data.message || 'Invitation sent successfully!', 'success');
      } else {
        showSnackbar(data.error || 'Failed to send invitation', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to send invitation. Please try again later.', 'error');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleUnlink = async () => {
    try {
      const response = await fetch(`${backendUrl}api/unlink-relationships/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ relationship_id: relationship.relationship_id }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to unlink');
      }
      setUnlinkDialogOpen(false);
      setIsLinked(false);
      setLinkedToName('');
      showSnackbar('Relationships unlinked successfully', 'success');
    } catch (error) {
      showSnackbar(error.message || 'Failed to unlink relationships', 'error');
    }
  };

  useEffect(() => {
    const fetchRelationship = async () => {
      try {
        let url;
        if (relationshipIdParam) {
          // Fetch specific relationship by ID
          url = `${backendUrl}api/relationship/${relationshipIdParam}`;
        } else {
          // Fetch current active relationship
          url = `${backendUrl}api/relationship/`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch relationship data');
        }

        const data = await response.json();
        setRelationship({
          relationship_id: data.relationship_id || '',
          startDate: data.date_started || '',
          endDate: data.date_ended || '',
          partnerName: data.partner_name || '',
          partnerEmail: data.partner_email || '',
          partnerSex: data.partner_sex || '',
          partnerDateOfBirth: data.partner_date_of_birth ? dayjs(data.partner_date_of_birth) : null,
          comments: data.comments || '',
          relationship_picture: data.relationship_picture || null,
          invitation_sent: data.invitation_sent || null,
        });
        setLinkCode(data.link_code || '');
        setSavedPartnerEmail(data.partner_email || '');

        // Fetch link status if relationship exists
        if (data.relationship_id) {
          fetchLinkStatus(data.relationship_id);
        }
      } catch (error) {
        console.error('Error fetching relationship data', error);
        showSnackbar('Failed to fetch relationship data', 'error');
      }
    };

    const fetchLastEndedRelationship = async () => {
      try {
        const response = await fetch(`${backendUrl}api/last-ended-relationship/`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setLastEndedRelationshipDate(data.date_ended);
          // Set minimum allowed date to the same day the last relationship ended
          if (data.date_ended) {
            setMinAllowedDate(data.date_ended);
          }
        } else {
          // No previous ended relationship found, allow any date within normal constraints
          setLastEndedRelationshipDate(null);
          setMinAllowedDate(null);
        }
      } catch (error) {
        console.error('Error fetching last ended relationship', error);
        setLastEndedRelationshipDate(null);
        setMinAllowedDate(null);
      }
    };

    fetchRelationship();
    fetchLastEndedRelationship();
  }, [backendUrl, authTokens, relationshipIdParam]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRelationship((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear errors when the user starts typing
    if (name === 'partnerName' && value.trim()) {
      setErrors((prevErrors) => ({ ...prevErrors, partnerName: '' }));
    }
    // Validate email on change
    if (name === 'partnerEmail') {
      if (value.trim() === '') {
        // Clear error if email is empty (not required)
        setErrors((prevErrors) => ({ ...prevErrors, partnerEmail: '' }));
      } else if (!validateEmail(value)) {
        setErrors((prevErrors) => ({ ...prevErrors, partnerEmail: 'Please enter a valid email address' }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, partnerEmail: '' }));
      }
    }
  };

  const handlePartnerDateChange = (date) => {
    setRelationship((prevState) => ({
      ...prevState,
      partnerDateOfBirth: date,
    }));
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    const twentyYearsAgo = new Date();
    twentyYearsAgo.setFullYear(twentyYearsAgo.getFullYear() - 20);
    const minDate = twentyYearsAgo.toISOString().split('T')[0]; // Get date 20 years ago in YYYY-MM-DD format

    if (selectedDate > today) {
      showSnackbar('Start date cannot be in the future', 'error');
      return;
    }

    if (selectedDate < minDate) {
      showSnackbar('Start date cannot be more than 20 years in the past', 'error');
      return;
    }

    // Check against last ended relationship date for new relationships
    if (!relationship.relationship_id && minAllowedDate && selectedDate < minAllowedDate) {
      showSnackbar(`Start date must be on or after ${minAllowedDate} (when your last relationship ended)`, 'error');
      return;
    }

    setRelationship((prevState) => ({
      ...prevState,
      startDate: selectedDate,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!relationship.partnerName.trim()) {
      newErrors.partnerName = 'Partner Name is required';
    }
    if (!relationship.startDate) {
      newErrors.startDate = 'Start Date is required';
    }
    // Validate email if provided
    if (relationship.partnerEmail.trim() && !validateEmail(relationship.partnerEmail)) {
      newErrors.partnerEmail = 'Please enter a valid email address';
    }
    // Check for overlapping relationships on new relationships
    if (!relationship.relationship_id && minAllowedDate && relationship.startDate && relationship.startDate < minAllowedDate) {
      newErrors.startDate = `Start date must be on or after ${minAllowedDate} (when your last relationship ended)`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showSnackbar('Please fill in the required fields', 'error');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}api/relationship/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({
          ...relationship,
          partnerDateOfBirth: relationship.partnerDateOfBirth ? relationship.partnerDateOfBirth.format('YYYY-MM-DD') : null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error('Failed to save relationship');
        }
      }

      const data = await response.json();
      showSnackbar('Relationship saved successfully', 'success');

      // Update the form fields with the response data, including relationship_id
      setRelationship({
        relationship_id: data.relationship_id || '',
        startDate: data.date_started || '',
        partnerName: data.partner_name || '',
        partnerEmail: data.partner_email || '',
        partnerSex: data.partner_sex || '',
        partnerDateOfBirth: data.partner_date_of_birth ? dayjs(data.partner_date_of_birth) : null,
        comments: data.comments || '',
        relationship_picture: data.relationship_picture || null,
        invitation_sent: data.invitation_sent || null,
      });
      setLinkCode(data.link_code || '');
      setSavedPartnerEmail(data.partner_email || '');

      // Fetch link status
      if (data.relationship_id) {
        fetchLinkStatus(data.relationship_id);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to save relationship', 'error');
      console.error('Save failed', error);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${backendUrl}api/relationship/`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({ relationship_id: relationship.relationship_id }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete relationship');
      }

      const data = await response.json();
      showSnackbar('Relationship deleted successfully', 'success');
      setRelationship({
        relationship_id: '',
        startDate: '',
        partnerName: '',
        partnerEmail: '',
        partnerSex: '',
        partnerDateOfBirth: null,
        comments: '',
        relationship_picture: null,
      });
    } catch (error) {
      showSnackbar('Failed to delete relationship', 'error');
      console.error('Delete failed', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleEndRelationship = async () => {
    if (!endDate) {
      showSnackbar('Please select an end date', 'error');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}api/relationship/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authTokens.access}`,
        },
        body: JSON.stringify({
          relationship_id: relationship.relationship_id,
          'end': true,
          'end_date': endDate.format('YYYY-MM-DD')
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to end relationship');
      }

      const data = await response.json();
      showSnackbar('Relationship ended successfully', 'success');

      // Reset the relationship data to show it's ended
      setRelationship({
        relationship_id: '',
        startDate: '',
        partnerName: '',
        partnerEmail: '',
        partnerSex: '',
        partnerDateOfBirth: null,
        comments: '',
        relationship_picture: null,
      });
    } catch (error) {
      showSnackbar('Failed to end relationship', 'error');
      console.error('End relationship failed', error);
    } finally {
      setEndDialogOpen(false);
      setEndDate(null);
    }
  };

  return (
    <>
    <SEO title="Relationship - Couplendar" />
    <Box sx={{
      minHeight: '100vh',
      background: '#F9F7F4',
      py: { xs: 3, md: 4 }
    }}>
      <Container maxWidth="md">
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#7B6B8D',
            mb: 4,
            textAlign: 'center'
          }}
        >
          {relationship.endDate ? 'Past Relationship' : 'Relationship'}
        </Typography>

        {/* Relationship Picture Section */}
        <Card sx={{
          mb: 3,
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          borderRadius: 3,
          border: 'none'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3
            }}>
              <Avatar
                src={relationshipPicturePreview || (relationship?.relationship_picture ? `${backendUrl}${relationship.relationship_picture}` : null)}
                sx={{
                  width: { xs: 80, md: 100 },
                  height: { xs: 80, md: 100 },
                  backgroundColor: '#C4856A'
                }}
              >
                💕
              </Avatar>
              {!relationship.endDate && (
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="relationship-picture-upload"
                    type="file"
                    onChange={handleRelationshipPictureChange}
                  />
                  <label htmlFor="relationship-picture-upload">
                    <IconButton color="primary" aria-label="upload picture" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  {relationshipPictureFile && (
                    <Button
                      variant="contained"
                      onClick={handleUploadRelationshipPicture}
                      disabled={uploadLoading}
                      sx={{ ml: 2 }}
                    >
                      {uploadLoading ? <CircularProgress size={20} /> : 'Upload'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Link Code Section - Only show for active relationships and pro users */}
        {relationship.relationship_id && !relationship.endDate && profile?.subscription_tier === 'pro' && (
          <Card sx={{
            mb: 3,
            background: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            borderRadius: 3,
            border: 'none'
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon /> Relationship Link
              </Typography>

              {isLinked ? (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <LinkIcon color="success" />
                    <Typography variant="body1" color="success.main">
                      Linked{linkedToName ? ` with ${linkedToName}` : ''}!
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={() => setUnlinkDialogOpen(true)}
                    sx={{ borderRadius: 2 }}
                  >
                    Unlink
                  </Button>
                </Box>
              ) : (
                <>
                  {linkCode ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Share this code with your partner:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          value={linkCode}
                          variant="outlined"
                          size="small"
                          inputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '2px' } }}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton onClick={handleCopyLinkCode} color="primary" title="Copy to clipboard">
                          <ContentCopyIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleGenerateLinkCode}
                      sx={{ mt: 2 }}
                    >
                      Generate Link Code
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    onClick={() => setLinkDialogOpen(true)}
                    sx={{
                      mt: 2,
                      ml: linkCode ? 2 : 0,
                      background: '#7B6B8D',
                    }}
                  >
                    Link with Partner
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Form Card */}
        <Card sx={{
          mb: 3,
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          borderRadius: 3,
          border: 'none'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {/* Show start and end dates for past relationships */}
            {relationship.endDate && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Started:</strong> {relationship.startDate ? dayjs(relationship.startDate).format('MMMM DD, YYYY') : 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Ended:</strong> {dayjs(relationship.endDate).format('MMMM DD, YYYY')}
                </Typography>
              </Box>
            )}

        <Box component="form" sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            name="startDate"
            variant="outlined"
            margin="normal"
            disabled={!!relationship.relationship_id || !!relationship.endDate}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                max: new Date().toISOString().split('T')[0], // Prevent future dates
                min: (() => {
                  // For new relationships, use minAllowedDate if available, otherwise use 20 years ago
                  if (!relationship.relationship_id && minAllowedDate) {
                    return minAllowedDate;
                  }
                  // For existing relationships or when no restriction, use 20 years ago
                  return new Date(new Date().setFullYear(new Date().getFullYear() - 20))
                    .toISOString()
                    .split('T')[0];
                })(),
              }
            }}
            value={relationship.startDate}
            onChange={handleDateChange}
            error={!!errors.startDate}
            helperText={errors.startDate || (!relationship.relationship_id && minAllowedDate ? `Must be on or after ${minAllowedDate} (when your previous relationship ended)` : '')}
            required
          />
          <TextField
            fullWidth
            label="Partner Name"
            name="partnerName"
            variant="outlined"
            margin="normal"
            value={relationship.partnerName}
            onChange={handleInputChange}
            error={!!errors.partnerName}
            helperText={errors.partnerName || `${relationship.partnerName.length}/100`}
            required
            disabled={!!relationship.endDate}
            inputProps={{ maxLength: 100 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Partner Email (optional)"
              type="email"
              name="partnerEmail"
              variant="outlined"
              value={relationship.partnerEmail}
              onChange={handleInputChange}
              error={!!errors.partnerEmail}
              helperText={errors.partnerEmail || `${relationship.partnerEmail.length}/254`}
              disabled={!!relationship.endDate}
              inputProps={{ maxLength: 254 }}
            />

            {/* Invitation Button - Only for pro users with active relationships and valid partner email */}
            {relationship.relationship_id &&
             !relationship.endDate &&
             profile?.subscription_tier === 'pro' &&
             isValidEmailForInvite() && (
              relationship.invitation_sent ? (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: '200px',
                  pt: 1.5
                }}>
                  <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    ✓ Invited
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={sendingInvitation ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                  onClick={handleSendInvitation}
                  disabled={sendingInvitation}
                  sx={{
                    minWidth: '200px',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#C4856A',
                    boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                    '&:hover': {
                      backgroundColor: '#B0745A',
                      boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {sendingInvitation ? 'Sending...' : 'Invite Partner'}
                </Button>
              )
            )}
          </Box>

          <FormControl fullWidth margin="normal" disabled={!!relationship.endDate}>
            <InputLabel>Partner Sex (optional)</InputLabel>
            <Select
              name="partnerSex"
              value={relationship.partnerSex}
              label="Partner Sex (optional)"
              onChange={handleInputChange}
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Partner Date of Birth (optional)"
              value={relationship.partnerDateOfBirth}
              onChange={handlePartnerDateChange}
              disabled={!!relationship.endDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal"
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Comments"
            name="comments"
            variant="outlined"
            margin="normal"
            multiline
            rows={4}
            value={relationship.comments}
            onChange={handleInputChange}
            disabled={!!relationship.endDate}
            inputProps={{ maxLength: 1000 }}
            helperText={`${relationship.comments.length}/1000`}
          />
          <input
            type='hidden'
            name="relationship_id"
            value={relationship.relationship_id}
          />
          <Box sx={{ mt: 3 }}>
            {/* Main action buttons */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: 2,
              mb: 2
            }}>
              {!relationship.endDate && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    px: 3,
                    background: '#7B6B8D',
                    boxShadow: 'none',
                    '&:hover': {
                      background: '#6A5A7A',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Save
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => setTimelineOpen(true)}
                startIcon={<TimelineIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: 3,
                  borderColor: '#7B6B8D',
                  color: '#7B6B8D',
                  '&:hover': {
                    borderColor: '#6A5A7A',
                    backgroundColor: 'rgba(184, 167, 212, 0.04)'
                  }
                }}
              >
                Timeline
              </Button>
              {relationship.relationship_id && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/chores-timeoff?relationshipId=${relationship.relationship_id}`)}
                  startIcon={<SettingsIcon />}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    px: 3,
                    borderColor: '#C4856A',
                    color: '#C4856A',
                    '&:hover': {
                      borderColor: '#B0745A',
                      backgroundColor: 'rgba(244, 181, 196, 0.04)'
                    }
                  }}
                >
                  Chores & Timeoff
                </Button>
              )}
            </Box>

            {/* Destructive action buttons - hide for past relationships */}
            {relationship.relationship_id && !relationship.endDate && (
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5
              }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    py: 0.8,
                    px: 2,
                    color: '#ff6b6b',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 107, 0.08)'
                    }
                  }}
                >
                  Delete Relationship
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setEndDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    py: 0.8,
                    px: 2,
                    color: '#d32f2f',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                >
                  End Relationship
                </Button>
              </Box>
            )}
          </Box>
        </Box>
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this relationship? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Relationship Confirmation Dialog */}
      <Dialog
        open={endDialogOpen}
        onClose={() => {
          setEndDialogOpen(false);
          setEndDate(null);
        }}
      >
        <DialogTitle>End Relationship</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please select the date when this relationship ended. The date must be between the day after the relationship started and today.
          </DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              minDate={relationship.startDate ? dayjs(relationship.startDate).add(1, 'day') : null}
              maxDate={dayjs()}
              sx={{ width: '100%', mt: 1 }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEndDialogOpen(false);
            setEndDate(null);
          }}>Cancel</Button>
          <Button
            onClick={handleEndRelationship}
            color="error"
            disabled={!endDate}
          >
            End Relationship
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setPartnerLinkCode('');
        }}
      >
        <DialogTitle>Link Relationships</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the link code that your partner shared with you to link your relationships together.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Partner's Link Code"
            value={partnerLinkCode}
            onChange={(e) => setPartnerLinkCode(e.target.value.toUpperCase())}
            variant="outlined"
            sx={{ mt: 1 }}
            inputProps={{
              maxLength: 8,
              style: { fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '2px', textTransform: 'uppercase' }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLinkDialogOpen(false);
            setPartnerLinkCode('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleLinkRelationships}
            variant="contained"
            disabled={!partnerLinkCode.trim() || partnerLinkCode.length !== 8}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Dialog */}
      <Dialog
        open={unlinkDialogOpen}
        onClose={() => setUnlinkDialogOpen(false)}
      >
        <DialogTitle>Unlink Calendars?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will disconnect your calendar{linkedToName ? ` from ${linkedToName}'s calendar` : ''}.
            You will both keep your existing data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUnlink} color="error" variant="contained">
            Unlink
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline Modal */}
      <RelationshipTimeline
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
      />

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
    </>
  );
};

export default RelationshipPage;